// dayjsUtil.ts
import dayjs, { Dayjs } from "dayjs";
// Register the "id" locale on the app's dayjs instance so localTzDate can
// switch to it per-call. Idempotent; a few pages also import it directly.
import "dayjs/locale/id";
import { currentLang } from "i18n";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import updateLocale from "dayjs/plugin/updateLocale";

// Extend Day.js with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(updateLocale);

// Jadwal (and Indonesian convention generally) starts the week on Monday,
// not dayjs's English-locale default of Sunday. Overriding the ACTIVE
// locale's weekStart (rather than switching locales) changes only
// .startOf("week")/.endOf("week")/.isSame(..., "week") — day/month name
// formatting is untouched. No GLOBAL dayjs.locale(...) call exists anywhere
// in the app (localTzDate applies its locale per-instance), so "en" is the
// actual active global locale this needs to target. The "id" locale ships
// weekStart 1 natively, so both languages get Monday weeks.
dayjs.updateLocale("en", { weekStart: 1 });

// Set default timezone globally (optional but recommended)
export const DEFAULT_TIMEZONE = "Asia/Jakarta";
export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";
export const DEFAULT_DATE_SUBMIT_FORMAT = "YYYY-MM-DD";

dayjs.tz.setDefault(DEFAULT_TIMEZONE);

/**
 * AntD bundles its OWN dayjs instance, so values coming out of its components
 * (DatePicker, the "Hari ini"/Today button, etc.) are Dayjs objects from that
 * instance — not ours. Since dayjs 1.11.10, `dayjs(x)` detects any Dayjs via
 * the `$isDayjsObject` marker and returns `x.clone()`, which preserves the
 * ORIGINATING instance. AntD's instance never had the utc/timezone plugins
 * extended, so the clone has no `.tz()`/`.utc()` and those calls throw. Reduce
 * any Dayjs to a native Date first so we always rebuild on our (plugin-extended)
 * instance.
 */
const toNativeDate = (dateInput?: string | Dayjs | Date) =>
  dayjs.isDayjs(dateInput) ? dateInput.toDate() : dateInput;

/**
 * Wrapper for Day.js that always returns a timezone-aware instance
 * @param dateInput - A date string, timestamp, or Date object
 * @param timezoneOverride - Optional override for a different timezone
 */
export const tzDate = (
  dateInput?: string | Dayjs | Date,
  timezoneOverride?: string,
) => {
  return dayjs(toNativeDate(dateInput)).tz(timezoneOverride || DEFAULT_TIMEZONE);
};

// Language-facing dates (day/month names) follow the ACTIVE UI language;
// tzDate stays on the global "en" locale for code-facing formats.
export const localTzDate = (
  dateInput?: string | Dayjs | Date,
  localeOverride?: string,
) => {
  return tzDate(dateInput).locale(localeOverride || currentLang());
};

export const utcDate = (dateInput?: string | Dayjs | Date) => {
  return dayjs(toNativeDate(dateInput)).utc();
};

// For a bare "YYYY-MM-DD" calendar-date string with no time-of-day meaning
// (e.g. a server-returned availability date) — deliberately NOT built on
// tzDate/utcDate, which both parse via dayjs(dateInput) FIRST and only
// convert timezone AFTERWARD. Verified empirically (not a spec assumption):
// dayjs parses a bare date-only string using the MACHINE'S LOCAL ZONE, not
// UTC, so on a browser set east of Jakarta (Bali/WITA +8, Papua/WIT +9,
// Australia, NZ...) that initial parse can already land on the wrong
// calendar day before any zone conversion runs — re-displaying it in another
// zone afterward doesn't undo that. Parsing directly in UTC mode sidesteps
// the local zone entirely, so day-of-week/day-of-month extraction from a
// pure calendar date stays correct no matter where the admin's machine is
// set. Use this (not tzDate) for any date-only string with no attached time.
export const dateOnly = (dateStr: string) =>
  dayjs.utc(dateStr, DEFAULT_DATE_SUBMIT_FORMAT);

// dateOnly's language-facing sibling (day/month names), same relationship
// localTzDate has to tzDate — for formatting a bare calendar-date string
// (e.g. "3 September" or a weekday name) in the active UI language, without
// dateOnly's whole reason for existing (skipping the browser-local parse)
// being defeated by going through the ordinary tzDate-based localTzDate.
export const localDateOnly = (dateStr: string, localeOverride?: string) =>
  dateOnly(dateStr).locale(localeOverride || currentLang());

export const now = () => tzDate();

// Year-to-date as a CustomMonthRange (1 Jan of the current year through the
// current month) — see AnalyticsRange's "ytd" doc comment (shared/types/
// analytics.ts) for why this reuses the custom-range picker's state instead
// of being its own server-side concept. Kept for SalesTrendPage.tsx, the one
// report whose custom-range picker stays month-only (its chart is bucketed
// by calendar month, so a day-precision range has no coherent chart shape).
export const getYtdMonthRange = () => ({
  from: `${now().year()}-01`,
  to: now().format("YYYY-MM"),
});

// Day-precision sibling of the above, for every other report's custom-range
// picker (CustomRangePicker.tsx) — 1 Jan of the current year through today,
// as exact dates rather than a month pair. Using day-precision here too
// (instead of getYtdMonthRange) means a page needs only ONE custom-range
// state slot: this and a manual pick both produce the same CustomDayRange
// shape, so there's no separate "which kind of custom range is active"
// bookkeeping to maintain.
export const getYtdDayRange = () => ({
  from: `${now().year()}-01-01`,
  to: now().format(DEFAULT_DATE_SUBMIT_FORMAT),
});

// A display helper, so it follows the active language like localTzDate
// ("05 Okt 2026" in Indonesian). Numeric formats read the same either way.
export const formatDate = (
  dateInput: string | Date | Dayjs,
  formatStr = DEFAULT_DATE_FORMAT,
) => localTzDate(dateInput).format(formatStr);

// A weekday's full name in the active language, 0 = Sunday (dayjs's .day()
// numbering): "Senin" / "Monday". For lists keyed by weekday, never a
// hardcoded array of names.
export const weekdayName = (day: number, lang = currentLang()): string =>
  dayjs().locale(lang).localeData().weekdays()[day];

// The server writes its date LABELS in English whatever the admin's language:
// an Analitik report's range_label, a trend month, the dashboard comparison,
// a payroll closing date. This rewrites their month names into the active
// language with dayjs's own names, so they read like every date the admin
// formats itself ("1 Jan – 5 Okt 2026", "Agt 2026").
const monthNameMaps = new Map<string, Map<string, string>>();
const monthNameMap = (lang: string): Map<string, string> => {
  const cached = monthNameMaps.get(lang);
  if (cached) return cached;
  const en = dayjs().locale("en").localeData();
  const target = dayjs().locale(lang).localeData();
  const map = new Map<string, string>();
  en.months().forEach((name, i) => map.set(name, target.months()[i]));
  // "May" is both a full and a short name; the full one already mapped it.
  en.monthsShort().forEach((name, i) => {
    if (!map.has(name)) map.set(name, target.monthsShort()[i]);
  });
  monthNameMaps.set(lang, map);
  return map;
};

let englishMonthPattern: RegExp | undefined;
export const localizeMonthNames = (text: string, lang = currentLang()): string => {
  if (lang === "en") return text;
  const map = monthNameMap(lang);
  // Longest first, so "June" matches before "Jun".
  englishMonthPattern ??= new RegExp(
    `\\b(${Array.from(map.keys())
      .sort((a, b) => b.length - a.length)
      .join("|")})\\b`,
    "g",
  );
  return text.replace(englishMonthPattern, (name) => map.get(name) ?? name);
};

export const combineDateTime = (
  date: Dayjs | undefined,
  time: Dayjs | undefined,
  scheduleTimezone = DEFAULT_TIMEZONE,
): Dayjs | undefined => {
  if (!date || !time) return undefined;
  const wallTime = `${date.format("YYYY-MM-DD")} ${time.format("HH:mm:ss")}`;
  return dayjs
    .tz(wallTime, "YYYY-MM-DD HH:mm:ss", scheduleTimezone)
    .millisecond(0);
};

// Is this instant on TODAY's Jakarta day? The undo window of a voided sale is
// the day it was voided, in the studio's timezone, which is exactly the
// comparison the server makes. Never compare a UTC date slice: that rolls
// over at 07:00 Jakarta.
export const sameJakartaDay = (iso?: string | null): boolean =>
  !!iso && tzDate(iso).isSame(now(), "day");
